import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { generateSecureQuestionnaireLink, updateQuestionnaireTokenTargetEmail } from '../../api/admin';

interface EmailConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (email: string, language: string, generatedLinks?: Array<{link: string}>) => Promise<void>;
  patientName: string;
  patientEmail: string;
  patientId: string;
  questionnaires: Array<{
    questionnaire: string;
    name: string;
    link?: string;
    entryCode?: string;
  }>;
  loading?: boolean;
}

export function EmailConfirmationModal({ 
  show, 
  onHide, 
  onConfirm, 
  patientName, 
  patientEmail,
  patientId,
  questionnaires,
  loading = false 
}: EmailConfirmationModalProps) {
  const [email, setEmail] = useState(patientEmail || '');
  const [language, setLanguage] = useState('el');
  const [error, setError] = useState('');
  const [generatedLinks, setGeneratedLinks] = useState<Array<{link: string}> | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Update email state when patientEmail prop changes
  useEffect(() => {
    if (patientEmail) {
      setEmail(patientEmail);
    }
  }, [patientEmail]);

  const generateSecureLinks = async () => {
    try {
      setIsGenerating(true);
      setError('');
      
      // Validate email before generating links
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address before generating links');
        return;
      }
      
      // Generate secure links for all questionnaires (link only, no OTP - NEW ARCHITECTURE)
      const linkPromises = questionnaires.map(async (questionnaire) => {
        const secureLink = await generateSecureQuestionnaireLink(questionnaire.questionnaire, patientId);
        return {
          questionnaire_id: secureLink.questionnaire_id,
          name: secureLink.name,
          link: secureLink.link
          // Note: No OTP code - it will be generated when patient clicks the link
        };
      });

      const links = await Promise.all(linkPromises);
      
      // CRITICAL FIX: Update each token with the current email address
      // This ensures OTP emails go to the right address even when links are copied manually
      try {
        const updatePromises = links.map(async (link) => {
          // Extract display token from link (e.g., "http://localhost:3000/questionnaire/AB12CD34")
          if (link.link && link.link.includes('/questionnaire/')) {
            const displayToken = link.link.split('/questionnaire/')[1];
            if (displayToken) {
              await updateQuestionnaireTokenTargetEmail(displayToken, email, language);
              console.log(`🔧 Updated token ${displayToken} with target email: ${email} and language: ${language}`);
            }
          }
        });
        
        await Promise.all(updatePromises);
      } catch (updateErr: any) {
        console.warn('Failed to update some token target emails:', updateErr);
        // Continue even if token updates fail - links will still work but OTP might go to stored email
      }
      
      setGeneratedLinks(links);
    } catch (err: any) {
      console.error('Error generating secure links:', err);
      setError('Failed to generate secure links: ' + (err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Auto-generate links if they don't exist
    if (!generatedLinks) {
      setError('Generating secure links...');
      await generateSecureLinks();
      return;
    }

    setError('');
    await onConfirm(email, language, generatedLinks);
  };

  const handleClose = () => {
    setEmail(patientEmail || '');
    setLanguage('el');
    setError('');
    setGeneratedLinks(null);
    setIsGenerating(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-envelope me-2"></i>
          Send Questionnaire Email
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <div className="alert alert-info">
          <h6 className="mb-2">
            <i className="bi bi-info-circle me-2"></i>
            Email Confirmation
          </h6>
          <p className="mb-2">
            You are about to send an automated email to <strong>{patientName}</strong> with 
            {questionnaires.length === 1 ? ' a questionnaire' : ` ${questionnaires.length} questionnaires`}.
          </p>
          {patientEmail && (
            <p className="mb-2 small text-success">
              <i className="bi bi-check-circle me-1"></i>
              Patient email: <strong>{patientEmail}</strong>
            </p>
          )}
          {!patientEmail && (
            <p className="mb-2 small text-warning">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Patient does not have a registered email address.
            </p>
          )}
          <p className="mb-0 small">
            The email will include secure links and OTP codes for each questionnaire.
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="bi bi-envelope me-2"></i>
                  Patient Email Address
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder={patientEmail ? "Enter email address" : "patient@example.com"}
                  disabled={loading}
                  className={error ? 'is-invalid' : ''}
                />
                <Form.Text className="text-muted">
                  The patient will receive the questionnaire email at this address.
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="bi bi-translate me-2"></i>
                  Email Language
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={loading}
                >
                  <option value="el">🇬🇷 Ελληνικά (Greek)</option>
                  <option value="en">🇺🇸 English</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Select the language for the email content.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-4">
            <h6 className="mb-3">
              <i className="bi bi-list-check me-2"></i>
              Questionnaires to be sent:
            </h6>
            <div className="card border-primary">
              <div className="card-body p-3">
                <div className="row">
                  {questionnaires.map((q, index) => (
                    <div key={q.questionnaire} className="col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-clipboard-data me-2 text-primary"></i>
                        <strong>{q.name}</strong>
                      </div>
                    </div>
                  ))}
                </div>
                
                {isGenerating && (
                  <div className="mt-3 text-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <span className="text-muted">Generating secure links...</span>
                  </div>
                )}

                {!generatedLinks && !isGenerating && (
                  <div className="mt-3 text-center">
                    <button 
                      type="button"
                      className="btn btn-primary"
                      onClick={generateSecureLinks}
                      disabled={!email.trim()}
                    >
                      <i className="bi bi-shield-lock me-2"></i>
                      Generate Secure Links
                    </button>
                    <div className="small text-muted mt-2">
                      Generate secure questionnaire links with the selected language ({language === 'el' ? 'Greek' : 'English'})
                    </div>
                  </div>
                )}

                {generatedLinks && (
                  <div className="mt-3">
                    <strong>🔒 Secure Links Generated:</strong>
                    <div className="mt-2 p-3 border rounded bg-light">
                      <p className="small text-muted mb-3">
                        {questionnaires.length} secure questionnaire link{questionnaires.length > 1 ? 's' : ''} generated. 
                        OTP codes will be sent automatically when patients click the links.
                      </p>
                      
                      {/* Display each questionnaire link */}
                      {generatedLinks.map((linkData, index) => (
                        <div key={index} className="mb-3">
                          <label className="form-label small text-muted">
                            {questionnaires[index]?.name || `Questionnaire ${index + 1}`} Link:
                          </label>
                          <div className="input-group">
                            <input 
                              type="text" 
                              className="form-control font-monospace small" 
                              value={linkData.link} 
                              readOnly 
                            />
                            <button 
                              className="btn btn-outline-secondary" 
                              type="button"
                              onClick={() => navigator.clipboard.writeText(linkData.link)}
                              title="Copy link to clipboard"
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <div className="mt-3 text-center">
                        <button 
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={generateSecureLinks}
                          disabled={isGenerating || loading}
                        >
                          <i className="bi bi-arrow-clockwise me-1"></i>
                          {isGenerating ? 'Generating...' : 'Regenerate Links'}
                        </button>
                        <div className="small text-muted mt-1">
                          Use this if you want to generate new links or changed the language
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="alert alert-success mt-3">
            <h6 className="mb-2">
              <i className="bi bi-check-circle me-2"></i>
              What the patient will receive:
            </h6>
            <ul className="mb-0 small">
              <li>Professional email with secure questionnaire links</li>
              <li>Clear instructions for accessing the questionnaires</li>
              <li>Information about the OTP verification process</li>
              <li>Contact information for support if needed</li>
            </ul>
          </div>

          <div className="alert alert-warning mt-3">
            <h6 className="mb-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Important Notes - New Two-Step Questionnaire System
            </h6>
            <ul className="mb-0 small">
              <li><strong>Two-Step Security:</strong> Patient clicks the link and receives a 6-digit OTP code via email</li>
              <li><strong>Link Validity:</strong> Questionnaire links are valid for 24 hours</li>
              <li><strong>OTP Process:</strong> OTP is sent automatically when patient opens the link (2-minute expiration)</li>
              <li><strong>Session Management:</strong> After successful OTP verification, patient gets access to questionnaire</li>
              <li><strong>Security Features:</strong> Failed OTP attempts are tracked; link locks after 5 failures</li>
            </ul>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
          style={{ 
            width: '120px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px'
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={(loading || isGenerating) || !email.trim() || !generatedLinks}
          style={{ 
            width: '200px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <i className="bi bi-envelope me-2"></i>
          Send Email to Patient
          {(loading || isGenerating) && (
            <span 
              className="spinner-border spinner-border-sm" 
              role="status" 
              aria-hidden="true"
              style={{
                marginLeft: '8px',
                flexShrink: 0
              }}
            ></span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
